// src/components/Pages/ForTeachers.tsx
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, addDoc, Timestamp } from 'firebase/firestore';
import { db } from '../../firebase/firebase';
 
const C = {
    primary:            '#6a37d4',
    primaryLight:       '#ae8dff',
    primaryContainer:   '#ede8ff',
    secondary:          '#006947',
    secondaryContainer: '#d1fae5',
    secondaryLight:     '#34d399',
    surface:            '#f3f6ff',
    surfaceLow:         '#eaf1ff',
    surfaceHigh:        '#d6e3f9',
    onBackground:       '#252f3c',
    onSurface:          '#252f3c',
    onSurfaceVariant:   '#525c6a',
    outlineVariant:     '#a4aebe',
};
 
const inputStyle: React.CSSProperties = {
    width: '100%', padding: '11px 14px',
    border: `1.5px solid ${C.outlineVariant}`,
    borderRadius: '10px', fontSize: '0.95rem',
    boxSizing: 'border-box',
    fontFamily: "'Plus Jakarta Sans', sans-serif",
    background: '#fff', color: C.onSurface,
    outline: 'none', transition: 'border-color 0.15s',
};
 
const faqs = [
    {
        q: "Is Scriblish free for teachers?",
        a: "The student-facing writing platform is completely free. Classroom management features for educators are in development — early-access teachers will receive special pricing when they launch."
    },
    {
        q: "How do students join my class?",
        a: "You'll get a class code to share with students. They enter the code during signup and are automatically added to your class roster."
    },
    {
        q: "Can I assign specific writing prompts?",
        a: "Yes. You'll be able to create assignments with custom prompts, due dates, and word count targets. Students will see these in their to-do list when they log in."
    },
    {
        q: "Is student data protected under FERPA?",
        a: "Yes. Scriblish is COPPA and FERPA compliant. Student data is never sold or shared with third parties. We collect only what's necessary for the platform to function."
    },
    {
        q: "Can students share their stories with each other?",
        a: "You control this. You can allow students to share within the class only, share publicly, or keep everything private. All published stories are moderated before going live."
    },
    {
        q: "What grade levels is Scriblish for?",
        a: "Scriblish is designed for grades 3–8 (ages 8–14). The writing tools scale well from basic narrative writing to more complex multi-chapter stories."
    },
];
 
const ForTeachers: React.FC = () => {
    const navigate = useNavigate();
    const [email, setEmail] = useState('');
    const [name, setName] = useState('');
    const [school, setSchool] = useState('');
    const [emailStatus, setEmailStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
    const [expandedFaq, setExpandedFaq] = useState<number | null>(null);
 
    const handleWaitlistSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!email || !email.includes('@')) { setEmailStatus('error'); return; }
        setEmailStatus('loading');
        try {
            await addDoc(collection(db, 'teacherWaitlist'), {
                email,
                name,
                school,
                timestamp: Timestamp.now(),
                status: 'pending',
                notified: false,
            });
            setEmailStatus('success');
            setEmail(''); setName(''); setSchool('');
        } catch {
            setEmailStatus('error');
            setTimeout(() => setEmailStatus('idle'), 3000);
        }
    };
 
    const features = [
        { icon: '👥', title: 'Classroom Groups',    desc: 'Create and manage multiple classes. Students join with a class code — no email required for young learners.' },
        { icon: '📋', title: 'Assignment Builder',  desc: 'Set writing prompts, due dates, word count goals, and genre requirements for each assignment.' },
        { icon: '📊', title: 'Progress Tracking',   desc: 'Monitor word counts, writing frequency, stories completed, and badges earned for every student.' },
        { icon: '💬', title: 'Teacher Feedback',    desc: 'Leave public encouragement or private notes on student work, visible only to the student.' },
        { icon: '🔒', title: 'Privacy Controls',    desc: 'Set class-wide sharing settings. Keep all work private, share within the class, or allow public publishing.' },
        { icon: '🏆', title: 'Class Leaderboards',  desc: 'Optional friendly competition with writing streaks, word count milestones, and badge achievements.' },
    ];
 
    const gettingStarted = [
        { step: '1', title: 'Sign up with your school email',  desc: 'Use your .edu or .k12.us email and you\'ll automatically get educator access. The signup takes under two minutes.' },
        { step: '2', title: 'Create your first class',         desc: 'Add a class name, grade level, and optional description. You\'ll get a shareable class code immediately.' },
        { step: '3', title: 'Students join with the code',     desc: 'Share the code with your class. Students enter it during signup and appear on your roster automatically.' },
        { step: '4', title: 'Create your first assignment',    desc: 'Set a prompt, due date, and any requirements. Students see it in their dashboard the moment you publish it.' },
        { step: '5', title: 'Track progress and give feedback', desc: 'Watch stories come in, leave notes on student work, and celebrate milestones from your teacher dashboard.' },
    ];
 
    return (
        <div style={{ minHeight: '100vh', background: C.surface, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
 
            {/* Hero */}
            <div style={{ background: `linear-gradient(135deg, ${C.secondaryContainer} 0%, ${C.surfaceLow} 100%)`, padding: '64px 24px 80px' }}>
                <div style={{ maxWidth: '760px', margin: '0 auto', textAlign: 'center' }}>
                    <div style={{ display: 'inline-block', background: C.secondary, color: '#fff', padding: '4px 14px', borderRadius: '9999px', fontSize: '0.75rem', fontWeight: '700', letterSpacing: '1px', textTransform: 'uppercase', fontFamily: "'Epilogue', sans-serif", marginBottom: '20px' }}>
                        For Educators
                    </div>
                    <h1 style={{ fontFamily: "'Epilogue', sans-serif", fontSize: '2.6rem', fontWeight: '900', color: C.onBackground, margin: '0 0 16px 0' }}>
                        Bring Creative Writing to Your Classroom
                    </h1>
                    <p style={{ fontSize: '1.05rem', color: C.onSurfaceVariant, margin: '0 0 32px 0', lineHeight: '1.7', maxWidth: '600px', marginLeft: 'auto', marginRight: 'auto' }}>
                        Scriblish gives teachers the tools to assign, monitor, and celebrate student writing — all in one safe, structured platform.
                    </p>
                    <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
                        <button
                            onClick={() => navigate('/signup-educator')}
                            style={{ background: `linear-gradient(135deg, ${C.secondary} 0%, ${C.secondaryLight} 100%)`, color: '#fff', border: 'none', borderRadius: '9999px', padding: '13px 32px', fontSize: '0.95rem', fontWeight: '700', cursor: 'pointer', fontFamily: "'Epilogue', sans-serif", boxShadow: `0 4px 14px ${C.secondary}40`, transition: 'opacity 0.15s, transform 0.15s' }}
                            onMouseEnter={e => { e.currentTarget.style.opacity = '0.9'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                            onMouseLeave={e => { e.currentTarget.style.opacity = '1'; e.currentTarget.style.transform = 'translateY(0)'; }}
                        >
                            Sign Up as an Educator
                        </button>
                        <a href="#waitlist" style={{ background: 'transparent', color: C.secondary, border: `2px solid ${C.secondary}`, borderRadius: '9999px', padding: '11px 28px', fontSize: '0.95rem', fontWeight: '700', cursor: 'pointer', fontFamily: "'Epilogue', sans-serif", textDecoration: 'none', display: 'inline-flex', alignItems: 'center', transition: 'all 0.15s' }}
                            onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = C.secondaryContainer; }}
                            onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
                        >
                            Join the Waitlist
                        </a>
                    </div>
                </div>
            </div>
 
            <div style={{ maxWidth: '800px', margin: '0 auto', padding: '64px 24px 80px' }}>
 
                {/* Classroom features */}
                <section style={{ marginBottom: '64px' }}>
                    <h2 style={{ fontFamily: "'Epilogue', sans-serif", fontSize: '1.8rem', fontWeight: '900', color: C.onBackground, margin: '0 0 8px 0' }}>
                        Classroom features
                    </h2>
                    <p style={{ fontSize: '0.95rem', color: C.onSurfaceVariant, margin: '0 0 32px 0', lineHeight: '1.6' }}>
                        Everything you need to run a structured creative writing programme — without the administrative overhead.
                    </p>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px' }}>
                        {features.map((f, i) => (
                            <div key={i} style={{ background: '#fff', borderRadius: '16px', padding: '24px', border: `1px solid ${C.surfaceHigh}`, display: 'flex', gap: '16px', alignItems: 'flex-start', transition: 'box-shadow 0.2s' }}
                                onMouseEnter={e => { (e.currentTarget as HTMLElement).style.boxShadow = '0 8px 24px rgba(0,0,0,0.08)'; }}
                                onMouseLeave={e => { (e.currentTarget as HTMLElement).style.boxShadow = 'none'; }}
                            >
                                <div style={{ width: '44px', height: '44px', borderRadius: '12px', background: C.secondaryContainer, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.4rem', flexShrink: 0 }}>{f.icon}</div>
                                <div>
                                    <div style={{ fontFamily: "'Epilogue', sans-serif", fontWeight: '800', fontSize: '0.95rem', color: C.onBackground, marginBottom: '4px' }}>{f.title}</div>
                                    <div style={{ fontSize: '0.85rem', color: C.onSurfaceVariant, lineHeight: '1.55' }}>{f.desc}</div>
                                </div>
                            </div>
                        ))}
                    </div>
                </section>
 
                {/* Curriculum alignment */}
                <section style={{ marginBottom: '64px' }}>
                    <div style={{ background: C.secondaryContainer, borderRadius: '20px', padding: '36px' }}>
                        <h2 style={{ fontFamily: "'Epilogue', sans-serif", fontSize: '1.5rem', fontWeight: '900', color: C.secondary, margin: '0 0 12px 0' }}>
                            📐 Curriculum alignment
                        </h2>
                        <p style={{ fontSize: '0.95rem', color: C.onSurface, margin: '0 0 20px 0', lineHeight: '1.7' }}>
                            Scriblish supports key ELA standards across grades 3–8, including narrative writing, story structure, descriptive language, and editing. The platform's built-in tools — Story Planner, Character Builder, and Chapter Editor — directly scaffold the writing process teachers already teach.
                        </p>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '10px' }}>
                            {[
                                'Narrative writing development',
                                'Plot and story structure',
                                'Character development',
                                'Descriptive and sensory language',
                                'Editing and revision process',
                                'Publishing for authentic audience',
                            ].map((item, i) => (
                                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.88rem', color: C.secondary, fontWeight: '600' }}>
                                    <div style={{ width: '18px', height: '18px', borderRadius: '50%', background: C.secondary, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.65rem', fontWeight: '700', flexShrink: 0 }}>✓</div>
                                    {item}
                                </div>
                            ))}
                        </div>
                    </div>
                </section>
 
                {/* Getting started */}
                <section style={{ marginBottom: '64px' }}>
                    <h2 style={{ fontFamily: "'Epilogue', sans-serif", fontSize: '1.8rem', fontWeight: '900', color: C.onBackground, margin: '0 0 8px 0' }}>
                        Getting started
                    </h2>
                    <p style={{ fontSize: '0.95rem', color: C.onSurfaceVariant, margin: '0 0 28px 0', lineHeight: '1.6' }}>
                        From signup to first assignment in under ten minutes.
                    </p>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        {gettingStarted.map(item => (
                            <div key={item.step} style={{ display: 'flex', gap: '16px', alignItems: 'flex-start', background: '#fff', borderRadius: '16px', padding: '20px 24px', border: `1px solid ${C.surfaceHigh}` }}>
                                <div style={{ width: '36px', height: '36px', borderRadius: '50%', background: C.secondaryContainer, color: C.secondary, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'Epilogue', sans-serif", fontWeight: '900', fontSize: '1rem', flexShrink: 0 }}>
                                    {item.step}
                                </div>
                                <div>
                                    <div style={{ fontFamily: "'Epilogue', sans-serif", fontWeight: '800', fontSize: '1rem', color: C.onBackground, marginBottom: '2px' }}>{item.title}</div>
                                    <div style={{ fontSize: '0.88rem', color: C.onSurfaceVariant, lineHeight: '1.55' }}>{item.desc}</div>
                                </div>
                            </div>
                        ))}
                    </div>
                </section>
 
                {/* Waitlist */}
                <section id="waitlist" style={{ marginBottom: '64px' }}>
                    <div style={{ background: '#fff', borderRadius: '20px', padding: '40px', border: `1px solid ${C.surfaceHigh}`, boxShadow: '0 4px 24px rgba(0,0,0,0.06)' }}>
                        <div style={{ display: 'inline-block', background: C.secondaryContainer, color: C.secondary, padding: '4px 12px', borderRadius: '9999px', fontSize: '0.75rem', fontWeight: '700', letterSpacing: '1px', textTransform: 'uppercase', fontFamily: "'Epilogue', sans-serif", marginBottom: '16px' }}>
                            Coming Soon
                        </div>
                        <h2 style={{ fontFamily: "'Epilogue', sans-serif", fontSize: '1.6rem', fontWeight: '900', color: C.onBackground, margin: '0 0 10px 0' }}>
                            Join the Educator Waitlist
                        </h2>
                        <p style={{ fontSize: '0.95rem', color: C.onSurfaceVariant, margin: '0 0 28px 0', lineHeight: '1.6' }}>
                            Full classroom management features are launching soon. Get on the list and you'll receive early access, special educator pricing, and onboarding support before the public launch.
                        </p>
 
                        {emailStatus === 'success' ? (
                            <div style={{ background: C.secondaryContainer, border: `1.5px solid ${C.secondary}30`, borderRadius: '14px', padding: '28px', textAlign: 'center' }}>
                                <div style={{ fontSize: '2.5rem', marginBottom: '10px' }}>✅</div>
                                <div style={{ fontFamily: "'Epilogue', sans-serif", fontWeight: '900', fontSize: '1.1rem', color: C.secondary, marginBottom: '6px' }}>You're on the list!</div>
                                <div style={{ fontSize: '0.9rem', color: C.onSurfaceVariant }}>We'll reach out with early access details before we launch.</div>
                            </div>
                        ) : (
                            <form onSubmit={handleWaitlistSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
                                    <div>
                                        <label style={{ display: 'block', marginBottom: '6px', fontSize: '0.8rem', fontWeight: '700', color: C.onSurfaceVariant, fontFamily: "'Epilogue', sans-serif" }}>Your Name</label>
                                        <input
                                            type="text" value={name} placeholder="Ms. Johnson"
                                            onChange={e => setName(e.target.value)}
                                            style={inputStyle}
                                            onFocus={e => { e.target.style.borderColor = C.secondary; }}
                                            onBlur={e  => { e.target.style.borderColor = C.outlineVariant; }}
                                        />
                                    </div>
                                    <div>
                                        <label style={{ display: 'block', marginBottom: '6px', fontSize: '0.8rem', fontWeight: '700', color: C.onSurfaceVariant, fontFamily: "'Epilogue', sans-serif" }}>School Name</label>
                                        <input
                                            type="text" value={school} placeholder="Lincoln Elementary"
                                            onChange={e => setSchool(e.target.value)}
                                            style={inputStyle}
                                            onFocus={e => { e.target.style.borderColor = C.secondary; }}
                                            onBlur={e  => { e.target.style.borderColor = C.outlineVariant; }}
                                        />
                                    </div>
                                </div>
                                <div>
                                    <label style={{ display: 'block', marginBottom: '6px', fontSize: '0.8rem', fontWeight: '700', color: C.onSurfaceVariant, fontFamily: "'Epilogue', sans-serif" }}>School Email *</label>
                                    <input
                                        type="email" value={email} required placeholder="you@school.edu"
                                        onChange={e => setEmail(e.target.value)}
                                        style={{ ...inputStyle, borderColor: emailStatus === 'error' ? '#dc2626' : C.outlineVariant }}
                                        onFocus={e => { e.target.style.borderColor = C.secondary; }}
                                        onBlur={e  => { e.target.style.borderColor = emailStatus === 'error' ? '#dc2626' : C.outlineVariant; }}
                                    />
                                    {emailStatus === 'error' && (
                                        <div style={{ marginTop: '6px', fontSize: '0.8rem', color: '#dc2626', fontWeight: '600' }}>Please enter a valid email address.</div>
                                    )}
                                </div>
                                <button
                                    type="submit"
                                    disabled={emailStatus === 'loading'}
                                    style={{ background: emailStatus === 'loading' ? C.outlineVariant : `linear-gradient(135deg, ${C.secondary} 0%, ${C.secondaryLight} 100%)`, color: '#fff', border: 'none', borderRadius: '9999px', padding: '13px 32px', fontSize: '0.95rem', fontWeight: '700', cursor: emailStatus === 'loading' ? 'not-allowed' : 'pointer', fontFamily: "'Epilogue', sans-serif", boxShadow: emailStatus === 'loading' ? 'none' : `0 4px 14px ${C.secondary}40`, transition: 'opacity 0.15s', alignSelf: 'flex-start' }}
                                    onMouseEnter={e => { if (emailStatus !== 'loading') e.currentTarget.style.opacity = '0.9'; }}
                                    onMouseLeave={e => { e.currentTarget.style.opacity = '1'; }}
                                >
                                    {emailStatus === 'loading' ? 'Submitting…' : 'Join the Waitlist'}
                                </button>
                            </form>
                        )}
                    </div>
                </section>
 
                {/* FAQ */}
                <section style={{ marginBottom: '64px' }}>
                    <h2 style={{ fontFamily: "'Epilogue', sans-serif", fontSize: '1.8rem', fontWeight: '900', color: C.onBackground, margin: '0 0 24px 0' }}>
                        Frequently asked questions
                    </h2>
                    <div style={{ background: '#fff', borderRadius: '20px', padding: '8px 32px', border: `1px solid ${C.surfaceHigh}` }}>
                        {faqs.map((faq, i) => (
                            <div key={i} style={{ borderBottom: i < faqs.length - 1 ? `1px solid ${C.surfaceHigh}` : 'none' }}>
                                <button
                                    onClick={() => setExpandedFaq(expandedFaq === i ? null : i)}
                                    style={{ width: '100%', textAlign: 'left', background: 'none', border: 'none', padding: '18px 0', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '16px' }}
                                >
                                    <span style={{ fontFamily: "'Epilogue', sans-serif", fontSize: '0.95rem', fontWeight: '800', color: C.onBackground, lineHeight: '1.4' }}>{faq.q}</span>
                                    <div style={{ width: '26px', height: '26px', borderRadius: '50%', background: expandedFaq === i ? C.secondary : C.surfaceLow, color: expandedFaq === i ? '#fff' : C.onSurfaceVariant, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1rem', fontWeight: '700', flexShrink: 0, transition: 'all 0.15s' }}>
                                        {expandedFaq === i ? '−' : '+'}
                                    </div>
                                </button>
                                {expandedFaq === i && (
                                    <div style={{ paddingBottom: '18px', fontSize: '0.9rem', color: C.onSurfaceVariant, lineHeight: '1.7' }}>
                                        {faq.a}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </section>
 
                {/* Contact */}
                <div style={{ background: C.secondaryContainer, borderRadius: '20px', padding: '36px', textAlign: 'center' }}>
                    <h2 style={{ fontFamily: "'Epilogue', sans-serif", fontSize: '1.3rem', fontWeight: '900', color: C.secondary, margin: '0 0 10px 0' }}>
                        Want to talk to someone first?
                    </h2>
                    <p style={{ fontSize: '0.95rem', color: C.onSurfaceVariant, margin: '0 0 20px 0', lineHeight: '1.6' }}>
                        Email us and we'll be happy to answer any questions about using Scriblish in your classroom.
                    </p>
                    <a
                        href="mailto:scriblishofficial@gmail.com"
                        style={{ display: 'inline-block', background: `linear-gradient(135deg, ${C.secondary} 0%, ${C.secondaryLight} 100%)`, color: '#fff', borderRadius: '9999px', padding: '12px 28px', fontSize: '0.95rem', fontWeight: '700', textDecoration: 'none', fontFamily: "'Epilogue', sans-serif", boxShadow: `0 4px 12px ${C.secondary}40`, transition: 'opacity 0.15s' }}
                        onMouseEnter={e => { (e.currentTarget as HTMLElement).style.opacity = '0.9'; }}
                        onMouseLeave={e => { (e.currentTarget as HTMLElement).style.opacity = '1'; }}
                    >
                        scriblishofficial@gmail.com
                    </a>
                </div>
 
                {/* Footer links */}
                <div style={{ marginTop: '48px', display: 'flex', gap: '20px', flexWrap: 'wrap', justifyContent: 'center' }}>
                    {[
                        { label: 'For Parents',          href: '/for-parents' },
                        { label: 'Safety Center',        href: '/safety' },
                        { label: 'Privacy Policy',       href: '/privacy-policy' },
                        { label: 'Community Guidelines', href: '/community-guidelines' },
                    ].map(({ label, href }) => (
                        <a key={label} href={href} style={{ color: C.primary, textDecoration: 'none', fontSize: '0.85rem', fontWeight: '600', fontFamily: "'Epilogue', sans-serif", transition: 'opacity 0.15s' }}
                            onMouseEnter={e => { (e.currentTarget as HTMLElement).style.opacity = '0.7'; }}
                            onMouseLeave={e => { (e.currentTarget as HTMLElement).style.opacity = '1'; }}
                        >
                            {label}
                        </a>
                    ))}
                </div>
            </div>
        </div>
    );
};
 
export default ForTeachers;